import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { CopyLanguageValuesResult, Language } from '../types/translation-types';
import {
  CreateLanguageRequest,
  UpdateLanguageRequest,
  LanguageUsageInfo,
} from '../types/language-management-types';

interface LanguageManagementStore {
  // Data
  languages: Language[];
  selectedLanguage: Language | null;
  totalCount: number;

  // UI States
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  submitError: string | null;

  // Copy-from-parent (compound language creation)
  isCopyingValues: boolean;
  copyValuesError: string | null;

  // Delete modal state
  deleteDialogOpen: boolean;
  languageToDelete: Language | null;
  deleteUsageInfo: LanguageUsageInfo | null;
  isDeleting: boolean;
  deleteError: string | null;

  // Blade state
  bladeOpen: boolean;

  // Actions
  fetchLanguages: () => Promise<void>;
  createLanguage: (data: CreateLanguageRequest) => Promise<Language>;
  updateLanguage: (id: number, data: UpdateLanguageRequest) => Promise<Language>;
  deleteLanguage: (id: number) => Promise<boolean>;
  copyLanguageValues: (
    newLanguageCode: string,
    baseLanguageCode: string
  ) => Promise<CopyLanguageValuesResult>;
  reorderLanguages: (reorderedLanguages: Language[]) => Promise<void>;
  setSelectedLanguage: (language: Language | null) => void;
  clearSubmitError: () => void;
  openDeleteDialog: (language: Language) => void;
  closeDeleteDialog: () => void;
  openBlade: (language?: Language) => void;
  closeBlade: () => void;
  reset: () => void;
}

export const useLanguageManagementStore = create<LanguageManagementStore>((set, get) => ({
  // Initial state
  languages: [],
  selectedLanguage: null,
  totalCount: 0,
  isLoading: false,
  isSubmitting: false,
  error: null,
  submitError: null,
  isCopyingValues: false,
  copyValuesError: null,
  deleteDialogOpen: false,
  languageToDelete: null,
  deleteUsageInfo: null,
  isDeleting: false,
  deleteError: null,
  bladeOpen: false,

  // Actions
  fetchLanguages: async () => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getAllLanguagesUseCase.execute();

      if (response.isSuccess) {
        set({
          languages: response.rows,
          totalCount: response.rows.length,
          isLoading: false,
        });
      } else {
        set({
          error: response.errorMessage || 'Failed to load languages',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load languages',
        isLoading: false,
      });
    }
  },

  createLanguage: async (data: CreateLanguageRequest) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const language = await services.createLanguageUseCase.execute(data);

      // Refresh the list
      await get().fetchLanguages();

      set({ isSubmitting: false, bladeOpen: false, selectedLanguage: null });
      return language;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create language';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  updateLanguage: async (id: number, data: UpdateLanguageRequest) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const language = await services.updateLanguageUseCase.execute(id, data);

      // Refresh the list
      await get().fetchLanguages();

      set({ isSubmitting: false, bladeOpen: false, selectedLanguage: null });
      return language;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update language';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  copyLanguageValues: async (newLanguageCode: string, baseLanguageCode: string) => {
    set({ isCopyingValues: true, copyValuesError: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.copyLanguageValuesUseCase.execute(
        newLanguageCode,
        baseLanguageCode
      );
      set({ isCopyingValues: false });
      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to copy language values';
      set({ isCopyingValues: false, copyValuesError: message });
      throw error;
    }
  },

  deleteLanguage: async (id: number) => {
    set({ isDeleting: true, deleteError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteLanguageUseCase.execute(id);

      if (response.isSuccess && response.deleted) {
        // Successfully deleted
        await get().fetchLanguages();
        set({
          isDeleting: false,
          deleteDialogOpen: false,
          languageToDelete: null,
          deleteUsageInfo: null,
        });
        return true;
      } else if (response.usageInfo) {
        // Language is in use - show usage info
        set({
          isDeleting: false,
          deleteUsageInfo: response.usageInfo,
        });
        return false;
      } else {
        // Other error
        set({
          isDeleting: false,
          deleteError: response.errorMessage || 'Failed to delete language',
        });
        return false;
      }
    } catch (error) {
      set({
        isDeleting: false,
        deleteError: error instanceof Error ? error.message : 'Failed to delete language',
      });
      return false;
    }
  },

  reorderLanguages: async (reorderedLanguages: Language[]) => {
    const previousLanguages = get().languages;

    // Optimistically update UI with new order and sortOrder values
    const updatedLanguages = reorderedLanguages.map((lang, index) => ({
      ...lang,
      sortOrder: index + 1,
    }));
    set({ languages: updatedLanguages });

    try {
      const services = servicesContainer.getServices();

      // Use batch reorder endpoint (20x faster than individual updates)
      // - Before: 26 languages × ~150ms = ~4 seconds
      // - After: 1 request × ~200ms = ~0.2 seconds
      await services.batchReorderLanguagesUseCase.execute(updatedLanguages);

      // Refresh to ensure we have the latest data
      await get().fetchLanguages();
    } catch (error) {
      // Revert to previous state on error
      set({
        languages: previousLanguages,
        error: error instanceof Error ? error.message : 'Failed to reorder languages',
      });
      throw error;
    }
  },

  setSelectedLanguage: (language: Language | null) => {
    set({ selectedLanguage: language });
  },

  clearSubmitError: () => {
    set({ submitError: null });
  },

  openDeleteDialog: (language: Language) => {
    set({
      deleteDialogOpen: true,
      languageToDelete: language,
      deleteUsageInfo: null,
      deleteError: null,
    });
  },

  closeDeleteDialog: () => {
    set({
      deleteDialogOpen: false,
      languageToDelete: null,
      deleteUsageInfo: null,
      deleteError: null,
    });
  },

  openBlade: (language?: Language) => {
    set({
      bladeOpen: true,
      selectedLanguage: language || null,
      submitError: null,
    });
  },

  closeBlade: () => {
    set({
      bladeOpen: false,
      selectedLanguage: null,
      submitError: null,
    });
  },

  reset: () => {
    set({
      languages: [],
      selectedLanguage: null,
      totalCount: 0,
      isLoading: false,
      isSubmitting: false,
      error: null,
      submitError: null,
      isCopyingValues: false,
      copyValuesError: null,
      deleteDialogOpen: false,
      languageToDelete: null,
      deleteUsageInfo: null,
      isDeleting: false,
      deleteError: null,
      bladeOpen: false,
    });
  },
}));
